import React, {FC, memo, ReactNode, useCallback, useEffect, useState} from "react";
import {Alert, Button, Modal} from "antd";
import {PrinterOutlined} from "@ant-design/icons";
import classnames from "classnames";
import PDFViewer from "pdf-viewer-reactjs";
import {red} from "@ant-design/colors";
import css from "./PDFViewerModal.less";
import {layoutPaddingBig, layoutPaddingRegular} from "../../styles/layout";
import LoadingContent from "../LoadingContent/LoadingContent";
import DraggableModal from "../DraggableModal/DraggableModal";
import {printPdfBlob} from "../../helpers/printPdf";

type PDFViewerProps = {
    title?: string;
    fileUrl?: string;
    invoiceBody: string;
    invoiceFileName?: string;
    handleOk: () => void;
    handleCancel: () => void;
    open: boolean;
    asModal?: boolean;
    kindOfDocument?: string;
    // Blendet die Seiten-Navigationsleiste (Vorherige/Nächste Seite + "Page X / Y")
    // aus. Für einseitige Dokumente wie Urkunden ist die Navigation sinnlos.
    singlePage?: boolean;
    // Hinweis oberhalb des PDFs, z.B. "Kein E-Mail-Empfänger hinterlegt".
    hint?: ReactNode;
    // Zusätzliche Buttons links von "Vorschau schließen".
    extraFooterButtons?: Array<ReactNode>;
    // Sperrt Dokumentinhalt und -aktionen, solange ein neues PDF geladen wird.
    loading?: boolean;
    // dimensions?: {
    //     width: number;
    //     height: number;
    // };
};

const PDFViewerModal: FC<PDFViewerProps> = ({
    title,
    handleOk,
    handleCancel,
    open,
    invoiceBody = "",
    invoiceFileName = "",
    asModal = true,
    kindOfDocument = "Rechnung",
    singlePage = false,
    hint,
    extraFooterButtons = [],
    loading = false,
    // dimensions,
}) => {
    const [preparedPdf, setPreparedPdf] = useState<{invoiceBody: string; fileURL: string; fileBlob: Blob} | null>(null);

    const base64ToBlob = useCallback((base64: string) => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);

        for (let i = 0; i < len; ++i) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const file = new Blob([bytes], {
            type: "application/pdf",
        });

        return file;
    }, []);

    useEffect(() => {
        let fileURL = "";

        if (!open || !invoiceBody || loading) {
            setPreparedPdf(null);
        } else {
            const fileBlob = base64ToBlob(invoiceBody);

            fileURL = URL.createObjectURL(fileBlob);

            setPreparedPdf({invoiceBody, fileURL, fileBlob});
        }

        return () => {
            if (fileURL) {
                URL.revokeObjectURL(fileURL);
            }
        };
    }, [invoiceBody, open, loading, base64ToBlob]);

    const documentReady = open && !loading && preparedPdf?.invoiceBody === invoiceBody;
    const fileURL = documentReady ? preparedPdf.fileURL : "";
    const fileBlob = documentReady ? preparedPdf.fileBlob : null;

    const renderContent = () => {
        if (!open) {
            return null;
        }

        if (loading || (invoiceBody && !documentReady)) {
            return <LoadingContent />;
        }

        if (fileURL && fileURL === "") {
            return <div>{"Leider haben wir diese Pdf nirgens finden können."}</div>;
        }

        // console.log("invoiceBody", invoiceBody);

        if (!fileURL || fileURL === "" || !open) {
            return null;
        }

        return (
            <>
                {hint ? <Alert type="info" showIcon message={hint} style={{marginBottom: layoutPaddingRegular}} /> : null}
                <PDFViewer
                    scale={1.5}
                    document={{
                        // url: fileURL,
                        base64: invoiceBody,
                    }}
                    hideNavbar={singlePage}
                    hideRotation
                    hideZoom
                    navbarOnTop
                    navigation={{
                        css: {
                            navbarWrapper: css.navbarWrapper,
                            previousPageBtn: css.previousPageBtn,
                            nextPageBtn: css.nextPageBtn,
                            pageIndicator: css.pageIndicator,
                        },
                    }}
                    canvasCss={css.pdfViewer}
                    loader={<LoadingContent />}
                />
            </>
        );
    };

    const handlePrint = () => {
        if (documentReady && fileBlob) {
            printPdfBlob(fileBlob);
        }
    };

    const footer = [
        <Button className={css.download} type="primary" key="download" href={documentReady ? fileURL : undefined} download={`${invoiceFileName}`} disabled={!documentReady}>
            {`${kindOfDocument} herunterladen`}
        </Button>,
        <Button key="print" icon={<PrinterOutlined />} onClick={handlePrint} disabled={!documentReady || !fileBlob}>
            {`${kindOfDocument} drucken`}
        </Button>,
        ...extraFooterButtons,
        <Button style={{color: red[3]}} key="close" className={css.cancel} onClick={handleCancel}>
            {"Vorschau schließen"}
        </Button>,
    ];

    if (asModal) {
        return (
            <DraggableModal
                data-testid="pdf-viewer-modal"
                draggable={true}
                boundsPadding={500}
                bodyStyle={{
                    padding: layoutPaddingRegular,
                    overflow: "auto",
                    // maxHeight: `calc(100vh - 220px - ${layoutPaddingBig})`,
                    maxHeight: `calc(100vh - 60px - ${Number(layoutPaddingBig.replace("px", "")) * 4}px)`,
                }}
                style={{
                    // top: 10,
                    top: layoutPaddingBig,
                    // height: "100vh",
                    // overflow: "auto",
                    // border: "2px solid red !important",
                }}
                width="250mm"
                className={classnames(css.root, css.withoutBorder)}
                open={open}
                title={title}
                onOk={handleOk}
                onCancel={handleCancel}
                destroyOnClose
                footer={footer}
            >
                {renderContent()}
            </DraggableModal>
        );
    }

    return renderContent();
};

export default memo(PDFViewerModal);
